import * as React from 'react'
import { ReactElement } from 'react'
import {
    Button,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogTitle,
    DialogTrigger,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import HammockDialog, { HammockDialogProps } from '../../Dialog/HammockDialog'
import { EntityType, SearchResult } from './EntityField'

type Props = {
    items: SearchResult[]
    externalType: EntityType
    onDismiss: () => void
    onSelect: (item: SearchResult) => void
}

type BodyProps = {
    items: SearchResult[]
    externalType: EntityType
    onSelect: (item: SearchResult) => void
}

const useStyles = makeStyles({
    row: {
        cursor: 'pointer',
    },
    idCell: {
        width: '120px',
    },
    codeCell: {
        width: '90px',
    },
})

function EntitySearchResultDialogBody(props: HammockDialogProps<BodyProps>): ReactElement {
    const { t: translate } = useTranslation()
    const styles = useStyles()

    const bodyProps = props.dialogBodyProps?.() ?? ({ items: [] } as BodyProps)
    const title = translate(`accounting.dimensions.search.${bodyProps.externalType.toString().toLowerCase().replaceAll(/[-_]/g, '')}`)

    return (
        <DialogBody>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Table size="small" aria-label={title}>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell className={styles.idCell}>{translate('base.table.header.id')}</TableHeaderCell>
                            <TableHeaderCell className={styles.codeCell}>{translate('base.table.header.number')}</TableHeaderCell>
                            <TableHeaderCell>{translate('base.table.header.code')}</TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(bodyProps.items || []).map(item => (
                            // Selecting clears the parent's search results, which closes the dialog; do not also call onDismiss (that would discard the selection).
                            <TableRow key={item.id} className={styles.row} onClick={() => bodyProps.onSelect(item)}>
                                <TableCell>{item.id}</TableCell>
                                <TableCell>{item.code}</TableCell>
                                <TableCell>{item.name}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">{translate('action.cancel')}</Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}

export default function EntitySearchResultDialog(props: Props): ReactElement {
    return (
        <HammockDialog<BodyProps>
            open={!!props.items}
            setOpen={open => {
                if (!open) {
                    props.onDismiss()
                }
            }}
            dialogBody={EntitySearchResultDialogBody}
            dialogBodyProps={() => ({ items: props.items, externalType: props.externalType, onSelect: props.onSelect })}
        />
    )
}
